import { format } from 'date-fns';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useWebSocket from 'react-use-websocket';
import { androidNotification, isAudioDisabledDevice } from '../controller/android-interface-controller';
import AssuntoAtendimentoEnum from '../Enum/AssuntosAtendimentoEnum';
import { socketUrl } from '../services/socket';
import { isWorking } from '../services/checkout-service/is-working';

const CHAT_SESSION_KEY = 'tecnozap_atendimentoId';
const AUDIO_DISABLED_NOTICE_MARKER = '[AUDIO_DISABLED_DEVICE]';
const RECONNECT_ALERT_GRACE_MS = 2000;
const IS_ANDROID_WEBVIEW =
  typeof window !== 'undefined' && typeof window.Android !== 'undefined';
const isWsTruthy = (value) =>
  value === true || value === 'True' || value === 'true' || value === 1 || value === '1';
const WSContext = createContext(null);

export function WSProvider({ children }) {
  const [audioDisabledByDevice, setAudioDisabledByDevice] = useState(() => isAudioDisabledDevice());
  const [chatMessage, setChatMessage] = useState(null);
  const [storedMessages, setStoredMessages] = useState([]);
  const [supportIsWriting, setSupportIsWriting] = useState(false);
  const [supportIsRecording, setSupportIsRecording] = useState(false);
  const [supportName, setSupportName] = useState('');
  const [status, setStatus] = useState('disconnected');
  const [supportLeftMessage, setSupportLeftMessage] = useState('');
  const [loadingAtendimento, setLoadingAtendimento] = useState(false);
  const [successStartService, setSuccessStartService] = useState(false);
  const [posicaoAtendimento, setPosicaoAtendimento] = useState(0);
  const [showModalLoginClient, setShowModalLoginClient] = useState(true);
  const [alertState, setAlertState] = useState(null);
  const [timer, setTimer] = useState(null);
  const [pararTimerAtivado, setPararTimerAtivado] = useState(false);
  const [reconnectionStatus, setReconnectionStatus] = useState(null);
  const [clientEstaSendoAtendido, setClientEstaSendoAtendido] = useState(false);
  const [chatResetCounter, setChatResetCounter] = useState(0);

  const mensagemAutomaticaEnviadaRef = useRef(false);
  const audioDisabledNoticeSentRef = useRef(false);
  const alertStateRef = useRef(alertState);
  const reconnectAlertTimeoutRef = useRef(null);
  const shouldShowReconnectAlertRef = useRef(false);

  useEffect(() => {
    alertStateRef.current = alertState;
  }, [alertState]);

  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 30;
    const syncDeviceAudioState = () => {
      const blocked = isAudioDisabledDevice();
      setAudioDisabledByDevice(blocked);
      if (blocked) return true;
      attempts += 1;
      return attempts >= maxAttempts;
    };

    if (syncDeviceAudioState()) return undefined;
    const timer = setInterval(() => {
      if (syncDeviceAudioState()) clearInterval(timer);
    }, 1000);

    const onFocus = () => syncDeviceAudioState();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') syncDeviceAudioState();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const endChatSession = useCallback((reason) => {
    // console.log(`[CLIENT-SESSION] Encerrando o chat. Motivo: ${reason}`);
    setSupportLeftMessage(reason);
    androidNotification('Atendimento Encerrado', reason);
    localStorage.removeItem(CHAT_SESSION_KEY);
    setAlertState('terminated');
    setTimer(null);
  }, []);

  useEffect(() => {
    let intervalId = null;
    if (timer && timer > 0) {
      intervalId = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    } else if (timer === 0) {
      endChatSession('A conexao com o suporte nao pode ser restaurada.');
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [timer, endChatSession]);

  const startNewChatSession = useCallback(() => {
    // console.log('[CLIENT-ACTION] Preparando para iniciar nova sessao.');
    setSupportLeftMessage('');
    setChatMessage(null);
    setStoredMessages([]);
    setAlertState(null);
    setPosicaoAtendimento(0);
    setSuccessStartService(false);
    setShowModalLoginClient(true);
    setClientEstaSendoAtendido(false);
    mensagemAutomaticaEnviadaRef.current = false;
    audioDisabledNoticeSentRef.current = false;
    setChatResetCounter((prev) => prev + 1);
  }, []);

  const reiniciarTimer = useCallback(() => {
    setPararTimerAtivado(true);
  }, []);

  const handleConnectionLogic = useCallback((message) => {
    switch (message.mensagemTipo) {
      case 'SESSION_CREATED': {
        // console.log(`ðŸŽŸï¸ Ticket recebido e salvo: ${message.atendimentoId}`);
        const previousAtendimentoId = localStorage.getItem(CHAT_SESSION_KEY);
        if (message.atendimentoId) {
          localStorage.setItem(CHAT_SESSION_KEY, message.atendimentoId);
          if (
            previousAtendimentoId
            && previousAtendimentoId !== 'undefined'
            && previousAtendimentoId !== message.atendimentoId
          ) {
            setChatResetCounter((prev) => prev + 1);
          }
        }
        setAlertState(null);
        setReconnectionStatus(null);
        setSupportLeftMessage('');
        return true;
      }
      case 'RECONNECT_SUCCESS': {
        // console.log(' Reconexao bem-sucedida!');
        shouldShowReconnectAlertRef.current = false;
        setSupportName(message.from);
        setSupportLeftMessage('');
        setClientEstaSendoAtendido(true);
        setShowModalLoginClient(false);
        setLoadingAtendimento(false);
        setSuccessStartService(true);
        setAlertState(null);
        setTimer(null);
        return true;
      }
      case 'RECONNECT_FAILED': {
        // console.log(' Falha na reconexao. O ticket era invalido ou expirou.');
        shouldShowReconnectAlertRef.current = false;
        endChatSession('Falha ao reconectar. Inicie um novo atendimento.');
        return true;
      }
      default:
        return false;
    }
  }, [endChatSession]);

  const ws = useWebSocket(socketUrl, {
    onOpen: () => {
      if (reconnectAlertTimeoutRef.current) {
        clearTimeout(reconnectAlertTimeoutRef.current);
        reconnectAlertTimeoutRef.current = null;
      }
      setStatus('connected');
      console.log(' Conectado ao WebSocket');
    },
    onMessage: (message) => {
      let respWS;
      try {
        respWS = JSON.parse(message.data);
        if (typeof respWS === 'string') respWS = JSON.parse(respWS);
      } catch (error) {
        console.error('Erro ao fazer parse da mensagem WebSocket:', error, 'Mensagem recebida:', message.data);
        return;
      }
      if (handleConnectionLogic(respWS)) return;
      switch (respWS.mensagemTipo) {
        case 'FORCE_DISCONNECT':
          // console.log(`[CLIENTE] Recebido comando de encerramento forcado. Motivo: ${respWS.reason}`);
          endChatSession(respWS.reason || 'O atendimento foi encerrado pelo suporte.');
          break;
        case 'SUPORTE_SOCKET_CREATED': {
          const userSession = JSON.parse(localStorage.getItem('tecnozap_user_session'));
          if (ws.sendJsonMessage) {
            ws.sendJsonMessage({
              mensagemTipo: 'CLIENTE_LOGIN',
              value: userSession.numSerie,
            });
          }
          break;
        }
        case 'SUPORTE_MENSAGEM_AUTOMATICA': {
          if (!clientEstaSendoAtendido) setClientEstaSendoAtendido(true);
          setShowModalLoginClient(false);
          break;
        }
        case 'SUPORTE_MENSAGEM': {
          setSupportName(respWS.from);
          const newMsg = {
            id: Date.now(),
            userType: 'suporte',
            userName: respWS.from,
            msgType: 'txt',
            msgText: respWS.value,
            urlFile: null,
            replyTo: respWS.replyTo || null,
            date: format(new Date(), 'HH:mm:ss'),
          };
          setChatMessage(newMsg);
          setStoredMessages((prev) => [...prev, newMsg]);
          androidNotification('Atendente enviou mensagem', respWS.value);
          if (!clientEstaSendoAtendido) setClientEstaSendoAtendido(true);
          break;
        }
        case 'SUPORTE_ESCREVENDO':
          setSupportIsWriting(isWsTruthy(respWS.typing));
          break;
        case 'SUPORTE_GRAVANDO_AUDIO':
          setSupportIsRecording(isWsTruthy(respWS.recording));
          break;
        case 'SUPORTE_SAIU':
        case 'CHAT_NOT_FOUND':
          endChatSession(respWS?.value || 'O suporte encerrou o chat.');
          break;
        case 'disconnect': {
          setSupportLeftMessage('');
          if (IS_ANDROID_WEBVIEW) {
            // No WebView, abrir seletor de arquivo pode atrasar eventos de socket.
            // Evita encerrar sessao por timeout curto durante envio de midia.
            setAlertState(null);
            setTimer(null);
          } else {
            setAlertState('temporary');
            setTimer(60);
            androidNotification('Conexao instavel', 'O suporte perdeu a conexao. Estamos tentando reconectar...');
          }
          break;
        }
        case 'SUPORTE_RECONECTADO': {
          // console.log(` Suporte "${respWS.from}" reconectou-se ao chat.`);
          setReconnectionStatus('returning');
          setTimeout(() => {
            setReconnectionStatus('returned');
            setTimeout(() => setReconnectionStatus(null), 2500);
          }, 1500);
          androidNotification('Atendimento retomado', `O atendente ${respWS.from} retomou o seu atendimento.`);
          setSupportLeftMessage('');
          setSupportName(respWS.from);
          setAlertState(null);
          setTimer(null);
          break;
        }
        case 'SUPORTE_RFILE': {
          const fileMsg = {
            id: Date.now(),
            userName: respWS.from,
            userType: 'suporte',
            msgText: respWS.fileName || 'Arquivo recebido',
            msgType: 'file',
            urlFile: respWS.value,
            replyTo: respWS.replyTo || null,
            date: format(new Date(), 'HH:mm:ss'),
          };
          setChatMessage(fileMsg);
          setStoredMessages((prev) => [...prev, fileMsg]);
          androidNotification('Nova Mensagem', 'Atendente te enviou um arquivo');
          break;
        }
        case 'SUPORTE_POSICAO':
          setPosicaoAtendimento(
            Number(respWS.position ?? respWS.posicao ?? respWS.value ?? 0) || 0
          );
          break;
        case 'SUPORTE_LOGIN_SUCESSO':
          setLoadingAtendimento(false);
          setSuccessStartService(true);
          break;
        case 'SUPORTE_LOGIN_ERROR':
          setLoadingAtendimento(false);
          break;
        default:
          console.warn('Tipo de mensagem nao reconhecida:', respWS.mensagemTipo);
      }
    },
    onClose: () => {
      setStatus('disconnected');
      console.log('Disconnected from App WS');
      setSupportIsWriting(false);
      setSupportIsRecording(false);
      setSupportName('');
      if (!IS_ANDROID_WEBVIEW && alertStateRef.current !== 'terminated') {
        shouldShowReconnectAlertRef.current = true;
        if (reconnectAlertTimeoutRef.current) {
          clearTimeout(reconnectAlertTimeoutRef.current);
        }
        reconnectAlertTimeoutRef.current = setTimeout(() => {
          if (
            shouldShowReconnectAlertRef.current
            && alertStateRef.current !== 'terminated'
          ) {
            setAlertState('reconnecting');
          }
        }, RECONNECT_ALERT_GRACE_MS);
      }
    },
    onError: (error) => {
      setStatus('error');
      console.error('WebSocket error:', error);
    },
    shouldReconnect: () =>
      // console.log('Verificando se deve reconectar...', { alertState: alertStateRef.current });
       alertStateRef.current !== 'terminated'
    ,
    reconnectAttempts: 50,
    reconnectInterval: 750,
  });

  useEffect(() => {
    if (!audioDisabledByDevice) return;
    if (!mensagemAutomaticaEnviadaRef.current) return;
    if (audioDisabledNoticeSentRef.current) return;
    if (!ws.sendJsonMessage) return;

    ws.sendJsonMessage({
      mensagemTipo: 'CLIENTE_MENSAGEM',
      value: `${AUDIO_DISABLED_NOTICE_MARKER}:rockchip YF_007F`,
    });
    audioDisabledNoticeSentRef.current = true;
  }, [audioDisabledByDevice, ws.sendJsonMessage]);


  useEffect(() => {
    if (status === 'connected') {
      const savedAtendimentoId = localStorage.getItem(CHAT_SESSION_KEY);
      if (savedAtendimentoId && savedAtendimentoId !== 'undefined') {
        if (!IS_ANDROID_WEBVIEW && shouldShowReconnectAlertRef.current) {
          setAlertState('reconnecting');
        }
        if (ws.sendJsonMessage) {
          ws.sendJsonMessage({
            mensagemTipo: 'CLIENTE_RECONECTAR',
            atendimentoId: savedAtendimentoId,
          });
        }
      }
    }
  }, [status, ws.sendJsonMessage]);

  useEffect(() => () => {
    if (reconnectAlertTimeoutRef.current) {
      clearTimeout(reconnectAlertTimeoutRef.current);
      reconnectAlertTimeoutRef.current = null;
    }
  }, []);

  const handleInactivityEndChat = useCallback(() => {
    // console.log('[CLIENT-ACTION] Encerrando chat por inatividade.');
    setAlertState('terminated');
    alertStateRef.current = 'terminated';
    if (ws.sendJsonMessage) {
      ws.sendJsonMessage({
        mensagemTipo: 'CLIENTE_ENCERRA_CHAT',
        value: 'O chat foi encerrado automaticamente por inatividade do cliente.',
      });
    }
    endChatSession('Sua sessao foi encerrada por inatividade.');
    const socketInstance = ws.getWebSocket();
    if (socketInstance && socketInstance.readyState === WebSocket.OPEN) {
      socketInstance.close();
    }
  }, [ws, endChatSession]);

  const handleClientEndChat = useCallback(() => {
    if (ws.sendJsonMessage) {
      ws.sendJsonMessage({
        mensagemTipo: 'CLIENTE_ENCERRA_CHAT',
        value: 'O cliente encerrou o atendimento.',
      });
    }
    endChatSession('Voce encerrou o atendimento.');
    alertStateRef.current = 'terminated';
    const socketInstance = ws.getWebSocket();
    if (socketInstance && socketInstance.readyState === WebSocket.OPEN) {
      socketInstance.close();
    }
  }, [ws, endChatSession]);

  const handleCancelQueue = useCallback(() => {
    // console.log('[CLIENT-ACTION] Cancelando fila e resetando o estado.');
    const fecharSessao = () => {
      endChatSession('Voce cancelou a solicitacao de atendimento.');
      alertStateRef.current = 'terminated';
      const socketInstance = ws.getWebSocket();
      if (socketInstance && socketInstance.readyState === WebSocket.OPEN) {
        socketInstance.close();
      }
      startNewChatSession();
    };

    const classificarMotivo = (statusWorking) => {
      if (!statusWorking || statusWorking.error) {
        return {
          motivoSaida: 'Servidor indisponivel durante espera na fila',
          origemDesistencia: 'indisponibilidade_servidor',
          haviaAtendenteOnline: false,
          foraHorario: false,
        };
      }

      if (statusWorking.online) {
        return {
          motivoSaida: 'Cancelou a solicitacao enquanto aguardava na fila',
          origemDesistencia: 'cancelamento_cliente',
          haviaAtendenteOnline: true,
          foraHorario: false,
        };
      }

      return {
        motivoSaida: 'Fora do horario de atendimento',
        origemDesistencia: 'fora_horario_atendimento',
        haviaAtendenteOnline: false,
        foraHorario: true,
      };
    };

    (async () => {
      try {
        const statusWorking = await isWorking();
        const motivo = classificarMotivo(statusWorking);

        if (ws.sendJsonMessage) {
          ws.sendJsonMessage({
            mensagemTipo: 'CLIENTE_ENCERRA_CHAT',
            value: 'Encerramento de fila solicitado pelo cliente.',
            ...motivo,
            disponibilidadeOnline: !!statusWorking?.online,
            disponibilidadeErro: !!statusWorking?.error,
            disponibilidadeMensagem: statusWorking?.message || '',
          });
        }
      } catch (error) {
        console.warn('[CLIENTE] Falha ao consultar disponibilidade antes de cancelar fila:', error);
      } finally {
        setTimeout(fecharSessao, 120);
      }
    })();
  }, [ws, endChatSession, startNewChatSession]);

  const value = useMemo(
    () => ({
      ws,
      chatMessage,
      storedMessages,
      status,
      supportIsWriting,
      supportIsRecording,
      supportName,
      supportLeftMessage,
      loadingAtendimento,
      successStartService,
      posicaoAtendimento,
      showModalLoginClient,
      alertState,
      timer,
      audioDisabledByDevice,
      handleInactivityEndChat,
      reiniciarTimer,
      reconnectionStatus,
      handleClientEndChat,
      handleCancelQueue,
      startNewChatSession,
      pararTimerAtivado,
      setPararTimerAtivado,
      chatResetCounter,
      handleClientTyping: (isTyping) => {
        setTimer(null);
        if (ws.sendJsonMessage) {
          ws.sendJsonMessage({ mensagemTipo: 'CLIENTE_ESCREVENDO', status: isTyping });
        }
      },
      handleClientRecordingStatus: (isRecording) => {
        setTimer(null);
        if (ws.sendJsonMessage) {
          ws.sendJsonMessage({ mensagemTipo: 'CLIENTE_GRAVANDO_AUDIO', status: isRecording });
        }
      },
      handleSendMessageText: (msgText, replyTo = null) => {
        setTimer(null);
        const newMsg = {
          id: Date.now(),
          userType: 'cliente',
          userName: 'Voce',
          msgType: 'txt',
          msgText,
          urlFile: null,
          replyTo,
          date: format(new Date(), 'HH:mm:ss')
        };
        if (ws.sendJsonMessage) {
          ws.sendJsonMessage({ mensagemTipo: 'CLIENTE_MENSAGEM', value: msgText, replyTo });
        }
        setStoredMessages((prev) => [...prev, newMsg]);
      },
      handleSendMessageAutomatica: (userData) => {
        if (mensagemAutomaticaEnviadaRef.current) return;
        const mensagemUnica = `
------------ MENSAGEM AUTOMATICA ----------
ASSUNTO: ${AssuntoAtendimentoEnum[userData.assunto]}
NOME: ${userData.name_user}
TELEFONE: ${userData.tel_user}
EMAIL: ${userData.email_user}
-------- FIM DA MENSAGEM AUTOMATICA --------
        `.trim();
        if (ws.sendJsonMessage) {
          ws.sendJsonMessage({ mensagemTipo: 'CLIENTE_MENSAGEM', value: mensagemUnica });
          if (audioDisabledByDevice) {
            ws.sendJsonMessage({
              mensagemTipo: 'CLIENTE_MENSAGEM',
              value: `${AUDIO_DISABLED_NOTICE_MARKER}:rockchip YF_007F`,
            });
            audioDisabledNoticeSentRef.current = true;
          }
        }
        mensagemAutomaticaEnviadaRef.current = true;
      },
      handleSendMessageFile: (pathFile, replyTo = null) => {
        setTimer(null);
        const fileMsg = {
          id: Date.now(),
          userName: 'Voce',
          userType: 'cliente',
          msgText: null,
          msgType: 'file',
          urlFile: pathFile,
          replyTo,
          date: format(new Date(), 'HH:mm:ss')
        };
        if (ws.sendJsonMessage) {
          ws.sendJsonMessage({ mensagemTipo: 'CLIENTE_FILE', value: pathFile, replyTo });
        }
        setStoredMessages((prev) => [...prev, fileMsg]);
      },
      handleStartAtendimento: () => {
        setLoadingAtendimento(true);
        setChatResetCounter((prev) => prev + 1);
        const rawSession = localStorage.getItem('tecnozap_user_session');
        let parsedSession = {};
        try {
          parsedSession = rawSession ? JSON.parse(rawSession) : {};
        } catch (err) {
          parsedSession = {};
        }
        if (ws.sendJsonMessage) {
          ws.sendJsonMessage({
            mensagemTipo: 'CLIENTE_START',
            value: JSON.stringify({
              ...parsedSession,
              clientType: IS_ANDROID_WEBVIEW ? 'webview' : 'browser',
            }),
          });
        }
      },
    }),
    [
      ws, chatMessage, storedMessages, status, supportIsWriting, supportIsRecording, supportName, supportLeftMessage, loadingAtendimento,
      successStartService, posicaoAtendimento, showModalLoginClient, alertState, timer, handleClientEndChat,
      reconnectionStatus, handleCancelQueue, startNewChatSession, handleInactivityEndChat, audioDisabledByDevice,
      reiniciarTimer, pararTimerAtivado, chatResetCounter,
    ]
  );

  return <WSContext.Provider value={value}>{children}</WSContext.Provider>;
}

export const useWSContext = () => {
  const context = useContext(WSContext);
  if (!context) throw new Error('useWSContext deve ser usado dentro de um WSProvider');
  return context;
};

